import {useApi} from "@contexts/hooks/api/useApi.ts";
import {_HealthCheckEndpoints, healthStatusType} from "@localization/siteMap/endpoints/_HealthCheckEndpoints.ts";
import {useCallback, useEffect, useRef, useState} from "react";

export interface IHealthStatus {
    api: healthStatusType,
    database: healthStatusType,
    lastUpdate: Date,
    lastDatabaseUpdate: Date,
    lastApiUpdate: Date,
    backendVersion?: string,
    frontendVersion?: string,

    [key: string]: healthStatusType | Date | string | undefined,
}

/**
 * Evita requisições simultâneas: não inicia uma nova enquanto a anterior não termina.
 * @see ApiProvider
 * @returns {{healthStatus: IHealthStatus, updateHealthStatus: () => Promise<void>}}
 */
export const useApiHealthStatus = () => {
    const {Get} = useApi();
    const [healthStatus, setHealthStatus] = useState<IHealthStatus>({
        api: "Degraded",
        database: "Degraded",
        lastUpdate: new Date(),
        lastDatabaseUpdate: new Date(),
        lastApiUpdate: new Date(),
        frontendVersion: import.meta.env['VITE_APP_VERSION']
    });

    // Flag para impedir concorrência
    const isUpdatingRef = useRef(false);
    const mountedRef = useRef(true);

    const updateHealthStatus = useCallback(async () => {
        if (isUpdatingRef.current) return; // já existe uma requisição em andamento
        isUpdatingRef.current = true;
        try {
            const x = (await Get(_HealthCheckEndpoints.All())) as unknown as IHealthStatus;
            if (!mountedRef.current) return;
            setHealthStatus(prevState => ({
                ...prevState,
                ...x,
                lastUpdate: new Date(),
                lastApiUpdate: new Date(),
                lastDatabaseUpdate: new Date(),
            }));
        } catch {
            if (!mountedRef.current) return;
            setHealthStatus(prevState => ({
                ...prevState,
                api: 'Error',
                database: 'Error',
                lastUpdate: new Date(),
                lastApiUpdate: new Date(),
                lastDatabaseUpdate: new Date(),
            }));
        } finally {
            isUpdatingRef.current = false;
        }
    }, [Get]);

    useEffect(() => {
        mountedRef.current = true;
        // dispara imediatamente
        updateHealthStatus();
        // agenda atualizações periódicas
        const interval = setInterval(() => {
            // só tenta se não houver uma chamada em andamento
            if (!isUpdatingRef.current) {
                updateHealthStatus();
            }
        }, 15000);
        return () => {
            mountedRef.current = false;
            clearInterval(interval);
        };
    }, [updateHealthStatus]);

    return {
        healthStatus,
        updateHealthStatus,
    };
}